import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { Spinner } from 'react-bootstrap';
import { Security } from '@okta/okta-react';
import { toRelativeUrl } from '@okta/okta-auth-js';
import { oktaAuth, isOAuthConfigured } from '@/config/okta-config';
import { OktaAuthProvider, LocalAuthProvider } from '@/common/contexts/auth-provider';
import { useAuth } from '@/common/hooks/use-auth';
import Layout from '@/common/components/layout';
import ReposList from '@/pages/repos/repos-list';
import RepoDetail from '@/pages/repos/repo-detail';
import PrsList from '@/pages/prs/prs-list';
import PrDetail from '@/pages/prs/pr-detail';
import ExceptionsList from '@/pages/exceptions/exceptions-list';
import ExceptionDetail from '@/pages/exceptions/exception-detail';
import Workspaces from '@/pages/onboarding/workspaces';
import WorkspaceRepos from '@/pages/onboarding/workspace-repos';
import OnboardWizard from '@/pages/onboarding/onboard-wizard';
import AuditList from '@/pages/audit/audit-list';
import Login from '@/pages/login/login';
import LoginCallbackPage from '@/pages/login/login-callback';

function AppShell() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Navigate to="/repos" replace />} />
        <Route path="/repos" element={<ReposList />} />
        <Route path="/repos/detail" element={<RepoDetail />} />
        <Route path="/onboarding" element={<Workspaces />} />
        <Route path="/onboarding/workspace" element={<WorkspaceRepos />} />
        <Route path="/onboarding/wizard" element={<OnboardWizard />} />
        <Route path="/prs" element={<PrsList />} />
        <Route path="/prs/detail" element={<PrDetail />} />
        <Route path="/audit" element={<AuditList />} />
        <Route path="/exceptions" element={<ExceptionsList />} />
        <Route path="/exceptions/detail" element={<ExceptionDetail />} />
        <Route path="*" element={<Navigate to="/repos" replace />} />
      </Routes>
    </Layout>
  );
}

/** Gate the app behind auth. In local no-auth mode isAuthenticated is always true. */
function Gated() {
  const { isAuthenticated, authEnabled, loading } = useAuth();
  // Wait for the OKTA SDK to settle before deciding — otherwise we'd bounce to /login
  // on the first render (authState null => not-yet-authenticated) even for a valid session.
  if (loading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status" />
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!authEnabled) return <AppShell />;
  return <AppShell />;
}

function OktaApp() {
  const navigate = useNavigate();
  const restore = (_: unknown, originalUri: string) =>
    navigate(toRelativeUrl(originalUri || '/', window.location.origin), { replace: true });
  return (
    <Security oktaAuth={oktaAuth!} restoreOriginalUri={restore}>
      <OktaAuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/login/callback" element={<LoginCallbackPage />} />
          <Route path="/*" element={<Gated />} />
        </Routes>
      </OktaAuthProvider>
    </Security>
  );
}

function LocalApp() {
  return (
    <LocalAuthProvider>
      <Routes>
        <Route path="/*" element={<AppShell />} />
      </Routes>
    </LocalAuthProvider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      {isOAuthConfigured ? <OktaApp /> : <LocalApp />}
    </BrowserRouter>
  );
}
